import { useMemo, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evaluationsApi } from '../praiseApi';
import type { AwardNomineeDetail } from '../praiseTypes';
import { sampleScores } from '../utils/samplePraise';

const ID_PREFIX = 'award-score';

/** A value as typed — empty, or a number 0..max with two decimals at most; null when it is not one. */
function parsePoints(raw: string, max: number): number | null {
  const text = raw.trim();
  if (text === '' || !/^\d+(\.\d{1,2})?$/.test(text)) return null;

  const value = Number(text);
  return value >= 0 && value <= max ? value : null;
}

/**
 * The CALLER's own score sheet for one nominee — every criterion, 0 to its maximum (product call,
 * 31 Oct 2026). Opened again, it is seeded from the sheet already saved, and saving REPLACES it.
 *
 * **A list of one repeated question**, so the Table Display View's form exception — the
 * ScoreWeightsModal's shape: the criterion is the tinted LEFT cell, the shared input the right.
 * A document criterion says whether the document was submitted, because that is what is being scored.
 */
export function ScoreNomineeModal({
  nominee,
  employeeName,
  onClose,
  onSaved,
}: {
  nominee: AwardNomineeDetail;
  employeeName: string | null;
  onClose: () => void;
  onSaved: (saved: AwardNomineeDetail) => void;
}) {
  const toast = useToast();
  const mine = nominee.scoreSheets.find((s) => s.isMine) ?? null;

  const [values, setValues] = useState<string[]>(() =>
    nominee.criteria.map((c) => {
      const line = mine?.lines.find((l) => l.awardCriterionId === c.id);
      return line != null ? String(line.points) : '';
    }),
  );
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const parsed = useMemo(() => nominee.criteria.map((c, i) => parsePoints(values[i] ?? '', c.maxPoints)), [nominee.criteria, values]);
  const allValid = parsed.every((p) => p !== null);
  const total = parsed.reduce<number>((sum, p) => sum + (p ?? 0), 0);
  const canSave = allValid && confirmed && !saving;

  const saveDisabledReason = saving
    ? null
    : !allValid
      ? 'Score every criterion, from 0 to its maximum.'
      : !confirmed
        ? 'Tick the confirm box to submit.'
        : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const saved = await evaluationsApi.saveScore(nominee.id, {
        lines: nominee.criteria.map((c, i) => ({ awardCriterionId: c.id, points: parsed[i] })),
      });

      toast.success(mine ? 'Your score was updated.' : 'Your score was saved.');
      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save your score.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleScores(nominee.criteria);
    setValues(nominee.criteria.map((c) => String(sample[c.id])));
  }

  const documentFor = (documentId: string | null | undefined) =>
    documentId == null ? null : nominee.documents.find((d) => d.awardCategoryDocumentId === documentId);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${mine ? 'Update your score' : 'Score'} — ${employeeName ?? nominee.categoryName}`}
      size="xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="text-xs text-slate-500">
          Your own sheet: score every criterion from 0 to its maximum, two decimals at most. Other evaluators score
          separately, and the nominee&apos;s result is the average of everyone&apos;s totals.
          {mine ? ' Saving replaces the score you gave before.' : ''}
        </p>

        <PreviewTable
          variant="table-display"
          rows={nominee.criteria.map((c, i) => {
            const document = documentFor(c.awardCategoryDocumentId);
            const label = `${c.name} (max ${c.maxPoints})`;

            return {
              label,
              value: (
                <div className="flex flex-col gap-1">
                  <TextField
                    labelHidden
                    id={`${ID_PREFIX}-lines.${i}.points`}
                    // The SAME string as the cell — the announced name must contain the visible label.
                    label={label}
                    required
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={c.maxPoints}
                    step="0.01"
                    className="font-mono"
                    description={c.description ?? `Points out of ${c.maxPoints}.`}
                    value={values[i] ?? ''}
                    onChange={(e) => setValues((current) => current.map((v, j) => (j === i ? e.target.value : v)))}
                    disabled={saving}
                    error={
                      (values[i] ?? '').trim() !== '' && parsed[i] === null
                        ? `Give a number from 0 to ${c.maxPoints}, with at most two decimals.`
                        : error?.fieldError(`lines.${i}.points`)
                    }
                  />
                  {c.awardCategoryDocumentId != null ? (
                    <span className="text-xs text-slate-400">
                      {document?.fileName ? `Submitted: ${document.fileName}` : 'Document not submitted'}
                    </span>
                  ) : null}
                </div>
              ),
            };
          })}
        />

        <p className="text-xs text-slate-600" aria-live="polite">
          Your total <span className="font-mono tabular-nums">{total.toFixed(2)}</span> of{' '}
          <span className="font-mono tabular-nums">{nominee.maxTotal}</span>
        </p>
      </div>
    </Modal>
  );
}
